import * as React from 'react';
import type { BeforeInstallPromptEvent } from 'utils/pwa';

let deferredPrompt: BeforeInstallPromptEvent | null = null;
const listeners = new Set<() => void>();
let listenerAttached = false;

function notifyListeners() {
  listeners.forEach((listener) => listener());
}

function attachDeferredInstallListener() {
  if (listenerAttached || typeof window === 'undefined') {
    return;
  }
  listenerAttached = true;

  window.addEventListener('beforeinstallprompt', (event: Event) => {
    event.preventDefault();
    deferredPrompt = event as BeforeInstallPromptEvent;
    notifyListeners();
  });
}

/** Call at app bootstrap so the install prompt is captured before any React mount. */
export function initPwaDeferredInstallPrompt(): void {
  attachDeferredInstallListener();
}

/** Capture Chromium `beforeinstallprompt` for the current page session. */
export function usePwaDeferredInstallPrompt(): BeforeInstallPromptEvent | null {
  const [prompt, setPrompt] = React.useState<BeforeInstallPromptEvent | null>(
    () => deferredPrompt
  );

  React.useEffect(() => {
    attachDeferredInstallListener();
    const sync = () => setPrompt(deferredPrompt);
    listeners.add(sync);
    sync();
    return () => {
      listeners.delete(sync);
    };
  }, []);

  return prompt;
}

export function clearPwaDeferredInstallPrompt(): void {
  deferredPrompt = null;
  notifyListeners();
}

export function getPwaDeferredInstallPrompt(): BeforeInstallPromptEvent | null {
  return deferredPrompt;
}
